import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface SearchSelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface SearchSelectProps {
  id?: string;
  value: string | null | undefined;
  onChange: (value: string, option: SearchSelectOption) => void;
  options: SearchSelectOption[];
  /** Rótulo do item selecionado quando ele não está na página de opções atual (busca no servidor). */
  selectedLabel?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  loading?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  /** Se informado, a busca é feita pelo chamador (servidor); senão filtra localmente. */
  onSearchChange?: (search: string) => void;
  /** Avisa quando o seletor abre/fecha (útil para carregar as opções só sob demanda). */
  onOpenChange?: (open: boolean) => void;
  footer?: (close: () => void) => ReactNode;
  className?: string;
}

const normalize = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Combobox acessível com busca — usado para cliente e itens de catálogo. */
export function SearchSelect({
  id,
  value,
  onChange,
  options,
  selectedLabel,
  placeholder = 'Selecione…',
  searchPlaceholder = 'Buscar…',
  emptyText = 'Nenhum resultado.',
  loading,
  disabled,
  invalid,
  onSearchChange,
  onOpenChange,
  footer,
  className,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    if (onSearchChange || !search.trim()) return options;
    const q = normalize(search.trim());
    return options.filter((o) => normalize(`${o.label} ${o.description ?? ''}`).includes(q));
  }, [options, search, onSearchChange]);

  useEffect(() => setActive(0), [search, open]);
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const current = options.find((o) => o.value === value);
  const label = current?.label ?? (value ? selectedLabel : undefined);

  const choose = (option: SearchSelectOption | undefined) => {
    if (!option || option.disabled) return;
    onChange(option.value, option);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        onOpenChange?.(o);
      }}
    >
      <PopoverTrigger asChild disabled={disabled}>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-invalid={invalid || undefined}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-input bg-white px-3 text-left text-sm',
            'focus:outline-none focus-visible:border-[#70aa96] focus-visible:ring-[3px] focus-visible:ring-success/10 disabled:cursor-not-allowed disabled:bg-muted disabled:opacity-70',
            invalid && 'border-destructive',
            className,
          )}
        >
          <span className={cn('truncate', !label && 'text-muted-foreground/80')}>{label ?? placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[260px]"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex items-center gap-2 border-b px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            autoFocus
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              onSearchChange?.(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, filtered.length - 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                choose(filtered[active]);
              }
            }}
            placeholder={searchPlaceholder}
            className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            aria-controls={listId}
            aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        <ul id={listId} ref={listRef} role="listbox" className="max-h-64 overflow-y-auto p-1">
          {filtered.length === 0 && !loading && (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyText}</li>
          )}
          {filtered.map((option, index) => (
            <li
              key={option.value}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={option.value === value}
              aria-disabled={option.disabled || undefined}
              onMouseEnter={() => setActive(index)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(option)}
              className={cn(
                'flex cursor-pointer items-start gap-2 rounded-md px-2 py-2 text-sm',
                index === active && 'bg-accent text-accent-foreground',
                option.disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <Check className={cn('mt-0.5 h-4 w-4 shrink-0', option.value === value ? 'opacity-100' : 'opacity-0')} />
              <span className="min-w-0">
                <span className="block truncate font-medium">{option.label}</span>
                {option.description && (
                  <span className="block truncate text-xs text-muted-foreground">{option.description}</span>
                )}
              </span>
            </li>
          ))}
        </ul>
        {footer && <div className="border-t p-1">{footer(() => setOpen(false))}</div>}
      </PopoverContent>
    </Popover>
  );
}
